/** @format */

import React, { useRef, useState } from "react";
import { parseChatData, formatTimestamp } from "../../../utils/chatHelpers";
import { useIntersectionLoadMore } from "../../../hooks/useIntersectionLoadMore";
import { ChatListLoadingSkeleton, ChatListItemSkeleton } from "../../../features/chats/ChatListItemSkeleton";
import MobileIconButton from "../../../components/MobileIconButton";
import ChatSearchField from "../../../components/chat/ChatSearchField";
import ChatEmptyState from "../../../components/chat/ChatEmptyState";
import { Button } from "@/components/ui/button";

const MobileChatList = ({
  onNewChat,
  newChatLoading,
  selectedChatId,
  onSelectChat,
  setShowChatView,
  onMenuClick,
  chatHistory = [],
  chatsLoading = false,
  hasMoreChats = false,
  chatsLoadingMore = false,
  onLoadMoreChats,
}) => {
  const [chatSearch, setChatSearch] = useState("");
  const chatListRef = useRef(null);
  const chatListScrollRef = useRef(0);

  const filteredChats = chatHistory.filter((chat) => {
    if (!chatSearch) return true;
    const data = parseChatData(chat.title);
    const q = chatSearch.toLowerCase();
    return (
      (data.primaryTitle || "").toLowerCase().includes(q) ||
      (chat.preview || "").toLowerCase().includes(q)
    );
  });

  const now = new Date();
  const todayChats = filteredChats.filter(
    (c) => (now - new Date(c.lastMessage || c.timestamp)) / (1000 * 60 * 60 * 24) < 1,
  );
  const olderChats = filteredChats.filter(
    (c) => (now - new Date(c.lastMessage || c.timestamp)) / (1000 * 60 * 60 * 24) >= 1,
  );

  const loadMoreSentinelRef = useIntersectionLoadMore({
    enabled: hasMoreChats && !chatsLoading,
    isLoading: chatsLoadingMore,
    onLoadMore: onLoadMoreChats,
    rootRef: chatListRef,
    observeKey: chatHistory.length,
    resetKey: selectedChatId,
  });

  const handleSelectChat = (chatId) => {
    chatListScrollRef.current = chatListRef.current?.scrollTop ?? 0;
    onSelectChat(chatId);
    setShowChatView("chat");
  };

  const renderChatItem = (chat) => {
    const data = parseChatData(chat.title);
    const isSelected = chat.id === selectedChatId;
    return (
      <div
        key={chat.id}
        onClick={() => handleSelectChat(chat.id)}
        className={`flex items-center justify-between px-4 py-3 mb-2 rounded-xl cursor-pointer transition-all border ${
          isSelected
            ? "bg-[#EFF4FF] border-[#C6DDF7]"
            : "bg-white border-[#E4E7EC] hover:bg-[#F9FAFB]"
        }`}
      >
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex justify-between items-start mb-1">
            <p className="text-[14px] font-[500] text-[#1D2739] truncate">
              {data.primaryTitle || "New Chat"}
            </p>
            <p className="text-[12px] text-[#98A2B3] mt-0.5">
              {formatTimestamp(chat.lastMessage || chat.timestamp)}
            </p>
          </div>
          <p className="text-[10px] text-[#1D2739] truncate">
            {chat.preview || "No messages yet"}
          </p>
        </div>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          className="flex-shrink-0"
        >
          <path
            d="M9 18L15 12L9 6"
            stroke="#98A2B3"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="grid grid-cols-[36px_1fr_auto] items-center gap-2 px-4 py-3 border-b border-[#E4E7EC] bg-white">
        <MobileIconButton onClick={onMenuClick} aria-label="Open menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M3 6H21M3 12H21M3 18H21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </MobileIconButton>
        <h1 className="text-[18px] font-[600] text-[#1D2739] text-center min-w-0">Chats</h1>
        <Button
          type="button"
          onClick={newChatLoading ? undefined : onNewChat}
          disabled={newChatLoading}
          className="box-border flex flex-shrink-0 justify-center items-center gap-1 h-9 min-h-[36px] max-h-9 px-3 rounded-3xl bg-[#2563EB] hover:bg-[#1C3BC7] active:bg-[#1C3BC7] active:scale-[0.98] text-white text-sm font-medium shadow-none justify-self-end"
        >
          {newChatLoading ? (
            <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-[15px] leading-none">+</span>
          )}
          New
        </Button>
      </div>

      {chatsLoading && chatHistory.length === 0 ? (
        <div className="flex-1 overflow-y-auto px-3 pt-3">
          <ChatListLoadingSkeleton rows={6} />
        </div>
      ) : filteredChats.length === 0 ? (
        chatHistory.length === 0 ? (
          <ChatEmptyState onAction={onNewChat} loading={newChatLoading} className="flex-1" />
        ) : (
          <p className="flex-1 flex items-center justify-center text-[13px] text-[#667185] px-6 text-center">
            No chats match your search.
          </p>
        )
      ) : (
        <div className="flex-1 overflow-y-auto" ref={chatListRef}>
          <div className="px-4 py-3 border-b border-[#E4E7EC]">
            <ChatSearchField
              size="docs"
              placeholder="Search chats"
              value={chatSearch}
              onChange={(e) => setChatSearch(e.target.value)}
              onClear={() => setChatSearch("")}
              clearable
              className="w-full"
            />
          </div>
          <div className="px-3 pt-3">
            {todayChats.length > 0 && (
              <>
                <p className="text-[11px] font-[600] text-[#344054] tracking-widest px-1 mb-2">
                  TODAY
                </p>
                {todayChats.map(renderChatItem)}
              </>
            )}
            {olderChats.length > 0 && (
              <>
                <p className="text-[11px] font-[600] text-[#344054] tracking-widest px-1 mt-4 mb-2">
                  OLDER
                </p>
                {olderChats.map(renderChatItem)}
              </>
            )}
            {hasMoreChats ? <div ref={loadMoreSentinelRef} className="h-px" aria-hidden /> : null}
            {chatsLoadingMore ? (
              <>
                <ChatListItemSkeleton />
                <ChatListItemSkeleton />
              </>
            ) : null}
            {!hasMoreChats && filteredChats.length >= 6 && !chatsLoadingMore ? (
              <p className="text-center text-[11px] text-[#98A2B3] py-3">You&apos;ve reached the end</p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileChatList;
